import * as p from '@clack/prompts'
import path from 'node:path'
import type { GenerateCommandOptions } from '@cli-types/core-engine/commands.js'
import type { ResolvedBetterstartConfig } from '@cli-types/core-engine/config.js'
import type { LoadedSchema } from '@cli-types/core-engine/schema-reader.js'
import { BARREL_SCOPE } from '@cli-constants/snapshots.js'
import { isInteractiveSession } from '@cli-utils/terminal.js'
import { listSchemaNames } from '@core-engine/schema/schema-reader/list-schema-names.js'
import { loadSchema } from '@core-engine/schema/schema-reader/load-schema.js'
import { SchemaNotFoundError } from '@core-engine/schema/schema-reader/schema-not-found-error.js'
import { validateLoadedSchema } from '@core-engine/schema/schema-reader/validate-loaded-schema.js'
import { ensureSnapshotGitFiles } from '@core-engine/snapshots/gitignore/ensure-snapshot-git-files.js'
import { clearTombstone } from '@core-engine/snapshots/store/clear-tombstone.js'
import { hasTombstone } from '@core-engine/snapshots/store/has-tombstone.js'
import { loadManifest } from '@core-engine/snapshots/store/load-manifest.js'
import { resolveAdminNamespace } from '@next-adapter/config/namespace/resolve-admin-namespace.js'
import { resolveProjectPaths } from '@next-adapter/config/paths/resolve-project-paths.js'
import { resolveConfig } from '@next-adapter/config/resolve-config.js'
import { runPostGenerate } from '@next-adapter/generators/post-generate/run-post-generate.js'
import { scanConflictPaths } from '@next-adapter/generators/post-generate/scan-conflict-paths.js'
import { ensureUserSchemaOwnership } from '@next-adapter/preset-runtime/ensure-user-schema-ownership.js'
import { applyBarrels } from './apply-barrels.js'
import { applySchemaGeneration } from './apply-schema-generation.js'
import { assertSnapshotStateOrExit } from './assert-snapshot-state-or-exit.js'
import { ensureMarkdownRendererDependencies } from './ensure-markdown-renderer-dependencies.js'
import { getGeneratedSchemaRoutePath } from './get-generated-schema-route-path.js'
import { printApplySummary } from './print-apply-summary.js'
import { schemaNeedsMarkdownRenderer } from './schema-needs-markdown-renderer.js'
import { validateIntegrationRequirements } from './validate-integration-requirements.js'

export async function runGenerateCommand(
  schemaName: string | undefined,
  options: GenerateCommandOptions
): Promise<void> {
  const cwd = options.cwd ? path.resolve(options.cwd) : process.cwd()
  const interactive = options.all
    ? options.interactive && isInteractiveSession()
    : isInteractiveSession()

  let config: ResolvedBetterstartConfig
  try {
    config = await resolveConfig(cwd)
  } catch (error) {
    p.log.error(`Error loading config: ${error instanceof Error ? error.message : String(error)}`)
    process.exit(1)
  }

  assertSnapshotStateOrExit(cwd)
  ensureSnapshotGitFiles(cwd)

  const paths = resolveProjectPaths(config)
  const schemasDir = path.join(cwd, ...paths.schemasDir.split('/'))

  if (options.all) {
    const schemaNames = listSchemaNames(schemasDir)
    if (schemaNames.length === 0) {
      p.log.error(`No schemas found in ${schemasDir}`)
      process.exit(1)
    }

    const failed: string[] = []
    const processed: string[] = []
    const processedRoutes: string[] = []
    let markdownDepsChecked = false
    const adminRoutePath = resolveAdminNamespace(config.frameworkConfig.next.namespace).routePath

    p.intro(`BetterStart Generator — regenerating ${schemaNames.length} schema(s)`)

    for (const name of schemaNames) {
      if (hasTombstone(cwd, name)) {
        if (loadManifest(cwd, name)) {
          clearTombstone(cwd, name)
          p.log.warn(`${name}: tombstone cleared (manifest present)`)
        } else {
          p.log.message(`${name}: skipped (tombstoned)`)
          continue
        }
      }

      try {
        const loaded = loadSchema(schemasDir, name)
        validateIntegrationRequirements(loaded, config)
        const validationErrors = validateLoadedSchema(loaded)
        if (validationErrors.length > 0) {
          throw new Error(validationErrors.join('; '))
        }

        const needsMarkdownRenderer = schemaNeedsMarkdownRenderer(loaded)
        if (!markdownDepsChecked && needsMarkdownRenderer) {
          await ensureMarkdownRendererDependencies(cwd, true)
          markdownDepsChecked = true
        }

        const result = await applySchemaGeneration(loaded, cwd, config, {
          force: options.force,
          acceptGenerated: options.acceptGenerated,
          interactive
        })
        ensureUserSchemaOwnership(cwd, name)
        printApplySummary(name, result.summary)
        processed.push(name)
        processedRoutes.push(getGeneratedSchemaRoutePath(loaded, adminRoutePath))
      } catch (error) {
        p.log.error(`${name}: ${error instanceof Error ? error.message : String(error)}`)
        failed.push(name)
      }
    }

    if (processed.length > 0) {
      const barrelSummary = await applyBarrels(cwd, config, {
        force: options.force,
        acceptGenerated: options.acceptGenerated,
        interactive
      })
      printApplySummary(BARREL_SCOPE, barrelSummary)
    }

    const conflictPaths = scanConflictPaths(cwd)
    if (processed.length > 0) {
      await runPostGenerate(cwd, processed[0] ?? 'admin', {
        skipMigration: options.skipMigration,
        hasConflicts: conflictPaths.length > 0,
        conflictPaths,
        adminRoutePath,
        schemaRoutePath: processedRoutes[0]
      })
    }

    if (failed.length > 0) {
      p.log.error(`Failed: ${failed.join(', ')}`)
      process.exit(1)
    }

    p.outro(`Regenerated ${processed.length}/${schemaNames.length} schema(s)`)
    return
  }

  if (!schemaName) {
    p.log.error('Error: schema name is required (or use --all)')
    process.exit(1)
  }

  let loaded: LoadedSchema
  try {
    loaded = loadSchema(schemasDir, schemaName)
  } catch (error) {
    if (error instanceof SchemaNotFoundError) {
      p.log.error(error.message)
    } else {
      p.log.error(`Error loading schema: ${error instanceof Error ? error.message : String(error)}`)
    }
    process.exit(1)
  }

  const validationErrors = validateLoadedSchema(loaded)
  if (validationErrors.length > 0) {
    p.log.error(
      ['Schema validation failed:', ...validationErrors.map((error) => `  - ${error}`)].join('\n')
    )
    process.exit(1)
  }

  p.intro(`BetterStart Generator — ${loaded.schema.name}`)
  const needsMarkdownRenderer = schemaNeedsMarkdownRenderer(loaded)
  await ensureMarkdownRendererDependencies(cwd, needsMarkdownRenderer)
  const result = await applySchemaGeneration(loaded, cwd, config, {
    force: options.force,
    acceptGenerated: options.acceptGenerated,
    interactive
  })
  ensureUserSchemaOwnership(cwd, loaded.schema.name)
  printApplySummary(loaded.schema.name, result.summary)

  const barrelSummary = await applyBarrels(cwd, config, {
    force: options.force,
    acceptGenerated: options.acceptGenerated,
    interactive
  })
  printApplySummary(BARREL_SCOPE, barrelSummary)

  const conflictPaths = scanConflictPaths(cwd)
  const adminRoutePath = resolveAdminNamespace(config.frameworkConfig.next.namespace).routePath
  await runPostGenerate(cwd, loaded.schema.name, {
    skipMigration: options.skipMigration,
    hasConflicts: conflictPaths.length > 0,
    conflictPaths,
    adminRoutePath,
    schemaRoutePath: getGeneratedSchemaRoutePath(loaded, adminRoutePath)
  })
  p.outro(`Generated ${loaded.schema.name}`)
}
